// Maquila Module Types
// Toll manufacturing warehouse management - isolated from main inventory

// ============================================
// BASE TYPES
// ============================================

export type MaquilaMovementType = "entry" | "exit" | "transfer";

export const MAQUILA_MOVEMENT_TYPE_LABELS: Record<MaquilaMovementType, string> =
  {
    entry: "Entrada",
    exit: "Salida",
    transfer: "Transferencia",
  };

export const MAQUILA_MOVEMENT_TYPE_COLORS: Record<MaquilaMovementType, string> =
  {
    entry: "bg-green-100 text-green-800",
    exit: "bg-red-100 text-red-800",
    transfer: "bg-blue-100 text-blue-800",
  };

// ============================================
// ENTITY TYPES
// ============================================

/** A client flagged as maquila, for the "Cliente maquila" selectors. */
export interface MaquilaClientOption {
  id: string;
  name: string;
  tax_id: string | null;
}

export interface MaquilaArea {
  id: string;
  code: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MaquilaItem {
  id: string;
  client_id: string;
  name: string;
  code: string | null;
  unit: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  client?: {
    id: string;
    name: string;
  };
}

export interface MaquilaInventory {
  id: string;
  maquila_item_id: string;
  maquila_area_id: string;
  quantity: number;
  created_at: string;
  updated_at: string;
  // Joined fields
  maquila_item?: MaquilaItem;
  maquila_area?: MaquilaArea;
}

export interface MaquilaMovement {
  id: string;
  movement_number: number;
  movement_type: MaquilaMovementType;
  maquila_item_id: string;
  client_id: string;
  from_area_id: string | null;
  to_area_id: string | null;
  quantity: number;
  unit: string;
  notes: string | null;
  reference_document: string | null;
  movement_date: string;
  performed_by: string | null;
  created_at: string;
  // Joined fields
  maquila_item?: MaquilaItem;
  client?: { id: string; name: string };
  from_area?: MaquilaArea;
  to_area?: MaquilaArea;
  performed_by_profile?: { id: string; name: string };
}

// ============================================
// FORM DATA TYPES
// ============================================

export interface MaquilaItemFormData {
  client_id: string;
  name: string;
  code?: string;
  unit: string;
  description?: string;
}

export interface MaquilaMovementFormData {
  movement_type: MaquilaMovementType;
  maquila_item_id: string;
  from_area_id?: string;
  to_area_id?: string;
  quantity: number;
  notes?: string;
  reference_document?: string;
}

// ============================================
// FILTER TYPES
// ============================================

export interface MaquilaMovementFilters {
  client_id?: string;
  maquila_item_id?: string;
  area_id?: string;
  movement_type?: MaquilaMovementType;
  date_from?: string;
  date_to?: string;
}

export interface MaquilaInventoryFilters {
  client_id?: string;
  area_id?: string;
}

// ============================================
// VIEW TYPES
// ============================================

export interface MaquilaInventoryByArea {
  area: MaquilaArea;
  items: Array<{
    item: MaquilaItem;
    quantity: number;
  }>;
  total_items: number;
}

export interface MaquilaInventoryByClient {
  client: { id: string; name: string };
  areas: Array<{
    area: MaquilaArea;
    items: Array<{
      item: MaquilaItem;
      quantity: number;
    }>;
  }>;
}

// ============================================
// CONSUMPTION OF ALIACE INSUMOS FOR MAQUILA
// ============================================
// Unlike the ledger above (which tracks the CLIENT's own goods), these entities
// model a recipe (BOM) for a maquila client's product and the consumption runs
// that DEDUCT Aliace's own ingredient stock (oils + raw materials). No finished
// good is booked; consumption is logged as a distinct `maquila_consume` movement.

export type MaquilaIngredientType = "oil" | "raw_material";

interface OilTypeRef {
  id: string;
  code: string | null;
  name: string;
  density_kg_per_liter: number | null;
  average_cost_clp: number | null;
}

interface RawMaterialRef {
  id: string;
  code: string | null;
  name: string;
  unit: string;
  average_cost_clp: number | null;
}

export interface MaquilaRecipeIngredient {
  id: string;
  recipe_id: string;
  ingredient_type: MaquilaIngredientType;
  oil_type_id: string | null;
  raw_material_id: string | null;
  quantity_per_unit: number;
  unit: string;
  waste_factor: number;
  notes: string | null;
  created_at: string;
  // Joined
  oil_type?: OilTypeRef | null;
  raw_material?: RawMaterialRef | null;
}

export interface MaquilaProductRecipe {
  id: string;
  client_id: string;
  product_name: string;
  product_code: string | null;
  output_unit: string;
  notes: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined
  client?: { id: string; name: string };
  ingredients?: MaquilaRecipeIngredient[];
}

export interface MaquilaConsumptionItem {
  id: string;
  consumption_id: string;
  ingredient_type: MaquilaIngredientType;
  oil_type_id: string | null;
  raw_material_id: string | null;
  quantity: number;
  unit: string;
  unit_cost_clp: number | null;
  total_cost_clp: number | null;
  plant: string;
  created_at: string;
  // Joined
  oil_type?: { id: string; code: string | null; name: string } | null;
  raw_material?: { id: string; code: string | null; name: string } | null;
}

export interface MaquilaConsumption {
  id: string;
  consumption_number: number;
  client_id: string;
  recipe_id: string | null;
  product_name: string;
  quantity_produced: number;
  plant: string;
  consumption_date: string;
  total_cost_clp: number;
  notes: string | null;
  reference_document: string | null;
  performed_by: string | null;
  created_at: string;
  // Joined
  client?: { id: string; name: string };
  performed_by_profile?: { id: string; name: string } | null;
  items?: MaquilaConsumptionItem[];
}

// A selectable Aliace ingredient (oil or raw material) for the recipe editor.
export interface MaquilaIngredientOption {
  id: string;
  ingredient_type: MaquilaIngredientType;
  code: string | null;
  name: string;
  unit: string; // 'kg' for oils, the raw material's unit otherwise
  density_kg_per_liter: number | null;
  average_cost_clp: number | null;
}

// ============================================
// FORM DATA TYPES
// ============================================

export interface MaquilaRecipeIngredientFormData {
  id?: string;
  ingredient_type: MaquilaIngredientType;
  oil_type_id?: string | null;
  raw_material_id?: string | null;
  quantity_per_unit: number;
  unit: string;
  waste_factor: number;
  notes?: string | null;
}

export interface MaquilaRecipeFormData {
  client_id: string;
  product_name: string;
  product_code?: string;
  output_unit: string;
  notes?: string;
  ingredients: MaquilaRecipeIngredientFormData[];
}

export interface MaquilaConsumptionLineInput {
  ingredient_type: MaquilaIngredientType;
  oil_type_id?: string | null;
  raw_material_id?: string | null;
  quantity: number; // final quantity to consume (per-unit x cantidad x waste)
  unit: string;
}

export interface MaquilaConsumptionInput {
  client_id: string;
  recipe_id?: string | null;
  product_name: string;
  quantity_produced: number;
  plant: string;
  lines: MaquilaConsumptionLineInput[];
  notes?: string;
  reference_document?: string;
  consumption_date?: string;
}

export interface MaquilaConsumptionFilters {
  client_id?: string;
  date_from?: string;
  date_to?: string;
}
